import { LitElement, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import '../checkbox/checkbox.element';
import '../icon/icon.element';
import { tableRowStyles } from './table-row.styles';
@customElement('cds-table-row')
export class TableRowElement extends LitElement {
  @query('slot') defaultSlot!: HTMLSlotElement;

  static styles = [tableRowStyles];

  @property({ type: Boolean, reflect: true })
  expandable = false;

  @property({ type: Boolean, reflect: true })
  expanded = false;

  @property({ type: Boolean, reflect: true })
  headerborder = false;

  @property({ type: Boolean, reflect: true })
  hoverable = false;

  @property({ type: String, reflect: true })
  role = 'row';

  @property({ type: Boolean, reflect: true })
  rowborder = false;

  @property({ type: Boolean, reflect: true })
  selectable = false;

  @property({ type: Boolean, reflect: true })
  selected = false;

  @property({ type: Boolean, reflect: true })
  zebra = false;

  /**
   * Optional background color for header rows. Because the table uses Shadow DOM,
   * host applications cannot easily override the internal header row background.
   * Setting this property (only meaningful when the row is inside a <cds-table-head>)
   * will apply an inline CSS custom property that the styles consume.
   * 
   * IMPORTANT: For theme compatibility, use the CSS light-dark() function:
   * - Recommended: headerbackground="light-dark(#f0f0f0, #404040)"
   * - Avoid: headerbackground="#f0f0f0" (only works in light theme)
   * 
   * Accepts any valid CSS color value including:
   * - CSS light-dark() function: "light-dark(#ffffff, #333333)"
   * - CSS custom properties: "var(--my-header-bg-color)"
   * - Static colors: "rgb(229,229,234)" or "#e5e5ea" (not theme-aware)
   * 
   * Example: <cds-table-row headerborder headerbackground="light-dark(rgb(229,229,234), rgb(80,80,85))">…
   */
  @property({ type: String, reflect: true })
  headerbackground: string | undefined;

  render() {
    return html`
      <div
        class="row"
        @expand=${this.handleExpand}
        @select=${this.handleSelect}
      >
        <slot></slot>
      </div>
      ${this.expanded
        ? html`
            <div class="row expanded">
              <slot name="expanded"></slot>
            </div>
          `
        : ''}
    `;
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('headerbackground')) {
      if (this.headerbackground) {
        this.style.setProperty('--cds-table-header-background', this.headerbackground);
      } else {
        this.style.removeProperty('--cds-table-header-background');
      }
    }
    if (
      changedProperties.has('expandable') ||
      changedProperties.has('expanded') ||
      changedProperties.has('selectable')
    ) {
      this.updateChildAttributes();
    }
  }

  private updateChildAttributes() {
    const slottedElements = this.defaultSlot.assignedElements({ flatten: true });
    const firstTableHeaderCell = slottedElements.find(
      (el) => el.tagName.toLowerCase() === 'cds-table-header-cell'
    );

    const firstTableCell = slottedElements.find(
      (el) => el.tagName.toLowerCase() === 'cds-table-cell'
    );

    if (firstTableHeaderCell) {
      this.updateSingleChildAttribute(firstTableHeaderCell, 'selectable')
    }

    if (firstTableCell) {
      this.updateSingleChildAttribute(firstTableCell, 'expandable')
      this.updateSingleChildAttribute(firstTableCell, 'expanded')
      this.updateSingleChildAttribute(firstTableCell, 'selectable')
    }
  }

  private updateSingleChildAttribute(child: Element, attribute: 'expandable' | 'expanded' | 'selectable') {
    if (this[attribute]) {
      child.setAttribute(attribute, '');
    } else {
      child.removeAttribute(attribute);
    }
  }

  private handleExpand() {
    this.expanded = !this.expanded;
  }

  private handleSelect() {
    this.selected = !this.selected;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cds-table-row': TableRowElement;
  }
}
